import { Description, Field, Input as HeadlessInput, Label } from '@headlessui/react';
import classnames from 'classnames';
import React, { forwardRef } from 'react';

import { useThemeClasses } from '@/lib/legacy-theme/theme-utils';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  helperText?: string;
  error?: string;
  success?: string;
  variant?: 'default' | 'error' | 'success';
  size?: 'sm' | 'md' | 'lg';
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  fullWidth?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, helperText, error, success, variant = 'default', size = 'md', startIcon, endIcon, fullWidth = false, className, disabled, ...props }, ref) => {
    // Base classes using theme-aware variables
    const baseClasses = 'w-full items-center border rounded-md transition focus:outline-none focus:ring-2 focus:ring-offset-2';

    // Theme-aware variant classes
    const variantClasses = {
      default: 'border-secondary-700 placeholder-text-muted focus:border-primary focus:ring-primary',
      error: 'border-error placeholder-text-muted focus:border-error focus:ring-error',
      success: 'border-success placeholder-text-muted focus:border-success focus:ring-success',
    };

    // Theme-aware size classes
    const sizeClasses = {
      sm: 'px-3 py-1 text-sm',
      md: 'px-3 py-2 text-base',
      lg: 'px-4 py-3 text-lg',
    };

    // Icon spacing classes
    const iconSpacingClasses = {
      sm: startIcon ? 'pl-xs' : endIcon ? 'pr-xs' : '',
      md: startIcon ? 'pl-sm' : endIcon ? 'pr-sm' : '',
      lg: startIcon ? 'pl-md' : endIcon ? 'pr-md' : '',
    };

    // Input padding adjustments for icons
    const inputPaddingClasses = {
      sm: startIcon && endIcon ? 'px-xs' : startIcon ? 'pl-sm pr-xs' : endIcon ? 'pr-sm pl-xs' : 'px-sm',
      md: startIcon && endIcon ? 'px-sm' : startIcon ? 'pl-md pr-sm' : endIcon ? 'pr-md pl-sm' : 'px-md',
      lg: startIcon && endIcon ? 'px-lg' : startIcon ? 'pl-lg pr-md' : endIcon ? 'pr-lg pl-md' : 'px-lg',
    };

    // Determine the actual variant based on error/success props
    const actualVariant = error ? 'error' : success ? 'success' : variant;

    // Combine input classes
    const inputClasses = useThemeClasses(
      classnames(baseClasses, variantClasses[actualVariant], sizeClasses[size], inputPaddingClasses[size], fullWidth && 'w-full flex-1', disabled && 'opacity-50 cursor-not-allowed', className),
    );

    // Icon classes
    const iconClasses = useThemeClasses(classnames('flex-shrink-0 text-text-muted', size === 'sm' ? 'w-4 h-4' : size === 'lg' ? 'w-6 h-6' : 'w-5 h-5'));

    // Container classes for icons
    const containerClasses = useThemeClasses(classnames('relative flex flex-col gap-2', fullWidth && 'w-full'));

    // Label classes
    const labelClasses = useThemeClasses(classnames('block font-semibold text-sm mb-xs', disabled && 'opacity-50'));

    // Helper text classes
    const helperTextClasses = useThemeClasses(classnames('mt-xs text-xs', error ? 'text-error' : success ? 'text-success' : 'text-muted'));

    return (
      <Field className={containerClasses}>
        {label && <Label className={labelClasses}>{label}</Label>}

        <div className="relative">
          {startIcon && (
            <div className={classnames('absolute left-0 top-1/2 transform -translate-y-1/2', iconSpacingClasses[size])}>
              <div className={iconClasses}>{startIcon}</div>
            </div>
          )}

          <HeadlessInput ref={ref} className={inputClasses} disabled={disabled} {...props} />

          {endIcon && (
            <div className={classnames('absolute right-0 top-1/2 transform -translate-y-1/2', iconSpacingClasses[size])}>
              <div className={iconClasses}>{endIcon}</div>
            </div>
          )}
        </div>

        {(helperText || error || success) && <Description className={helperTextClasses}>{error || success || helperText}</Description>}
      </Field>
    );
  },
);

Input.displayName = 'Input';

export default Input;
